<template>
  <button
    v-if="!isLink(variant)"
    class="iai-btn relative flex min-h-[2.5rem] flex-none items-center justify-center border-2 border-solid border-theme-3 px-3.5 py-2 font-medium"
    :class="`variant-${variant} size-${size}`"
    :disabled="disabled"
    :type="btnType"
  >
    <div v-if="isLoading" class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
      <IAILoader variant="PULSE" :color="loaderColor" />
    </div>

    <template v-else>
      <div v-if="iconSrc && iconSide === 'start'">
        <img :src="iconSrc" class="mr-2 h-3 w-3" />
      </div>

      <span :class="{ 'opacity-0': isLoading }" data-test-button-label>{{ label }}</span>

      <div v-if="iconSrc && iconSide === 'end'">
        <img :src="iconSrc" class="ml-2 h-3 w-3" />
      </div>
    </template>
  </button>

  <div
    v-else-if="isLink(variant)"
    :class="[`variant-${variant}`, { disabled: disabled }]"
    class="flex flex-row items-center"
  >
    <img v-if="iconSrc && iconSide === 'start'" :src="iconSrc" class="mr-2 h-3 w-3" />

    <span>{{ label }} </span>

    <img v-if="iconSrc && iconSide === 'end'" :src="iconSrc" class="ml-2 h-3 w-3" />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { type ButtonHTMLAttributes } from "vue";
import type { LoaderColor } from "@/core-ui-vue";

import IAILoader from "../IAILoader/IAILoader.vue";

type BtnVariant =
  | "primary"
  | "secondary"
  | "link-underlined"
  | "link"
  | "blueish"
  | "white-border"
  | "gray"
  | "outline";

type BtnSize = "small" | "medium" | "large" | "extra-large";
type IconSide = "start" | "end";
const props = withDefaults(
  defineProps<{
    label: string;
    disabled?: boolean;
    variant?: BtnVariant;
    btnType?: ButtonHTMLAttributes["type"];
    size?: BtnSize;
    isLoading?: boolean;
    iconSrc?: string;
    iconSide?: IconSide;
  }>(),
  {
    disabled: false,
    variant: "primary",
    btnType: "button",
    isLoading: false,
    size: "medium",
  }
);

type LinkBtn = Extract<BtnVariant, "link" | "link-underlined">;
function isLink(variant: BtnVariant): variant is LinkBtn {
  return ["link", "link-underlined"].includes(variant);
}
const loaderColorMap: Record<Exclude<BtnVariant, LinkBtn>, LoaderColor> = {
  primary: "primary",
  secondary: "secondary",
  blueish: "primary",
  "white-border": "primary",
  gray: "primary",
  outline: "primary",
};

const loaderColor = computed(() => {
  if (props.disabled) return "disabled";
  if (isLink(props.variant)) return;
  return loaderColorMap[props.variant];
});
</script>

<style lang="scss" scoped>
.variant- {
  &primary {
    @apply bg-theme-3 text-white active:border-theme-3c active:bg-theme-3c hover-never:border-theme-3b hover-never:bg-theme-3b;

    &[disabled] {
      @apply border-theme-disabled bg-theme-disabled text-theme-disabled-b;
    }
  }

  &secondary {
    @apply bg-white text-theme-3 active:bg-theme-3c-secondary hover-never:bg-theme-3b-secondary;

    &[disabled] {
      @apply border-theme-disabled-c text-theme-disabled-c hover-never:bg-white;
    }
  }

  &link-underlined {
    @apply text-theme-3 underline active:text-theme-3c hover-never:cursor-pointer hover-never:text-theme-3b;

    &.disabled {
      @apply border-theme-disabled-b text-theme-disabled-c no-underline hover-never:cursor-not-allowed;
    }
  }

  &link {
    @apply text-theme-3 no-underline active:text-theme-3c hover-never:cursor-pointer hover-never:text-theme-3b hover-never:underline;

    &.disabled {
      @apply border-theme-disabled-b text-theme-disabled-c no-underline hover-never:cursor-not-allowed;
    }
  }

  &blueish {
    @apply border-none bg-theme-1 text-white;

    &[disabled] {
      color: #162a4a;
      background-color: #2b4673;
      border: none;
    }
  }

  &white-border {
    @apply border-white text-white;
    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }
    &[disabled] {
      opacity: 0.6;
      @apply hover-never:cursor-not-allowed;
    }
  }

  &gray {
    @apply border-none  bg-theme-disabled text-theme-1 hover-never:bg-theme-disabled-c;
  }

  &outline {
    @apply rounded-lg border-1 border-theme-disabled-b bg-white text-theme-1 hover-never:bg-gray-200;

    &[disabled] {
      @apply opacity-60 hover-never:cursor-not-allowed;
    }
  }

  // This will apply color transitions to all of the variants

  &primary,
  &secondary,
  &link,
  &link-underlined,
  &white-border,
  &outline {
    transition:
      background-color,
      color,
      0.12s cubic-bezier(0, 0.04, 0.03, 0.6);

    &[disabled],
    &.disabled {
      transition:
        background-color,
        color,
        0.12s cubic-bezier(0, 0.04, 0.03, 0.6);
    }
  }
}

.size- {
  &small {
    @apply rounded-lg text-sm font-normal;
  }
  &medium {
    @apply rounded-lg text-base;
  }

  &large {
    @apply rounded-lg text-lg font-semibold;
  }

  &extra-large {
    @apply rounded-lg text-xl font-semibold;
  }
}
</style>
